import LoadingDots from "components/componentLibrary/LoadingDots";
import { useUploadImage, usePollImages } from "hooks/useUploadImage";
import React, { ChangeEvent, useState, useEffect } from "react";
import { ImageAttachedToKind, ImageSourcePlatform2 } from "types/globalTypes";
import styles from "./styles.module.scss";

type Props = {
  attachedToId: string;
  attachedToKind: ImageAttachedToKind;
  sourcePlatform?: ImageSourcePlatform2;
  onComplete?: Function;
  className?: string;
  "data-qa"?: string;
  labelCTA?: string;
};

const UploadImagesButtons = ({
  attachedToId,
  attachedToKind,
  sourcePlatform = ImageSourcePlatform2.WEB,
  onComplete = () => {},
  className = `${styles.labelButton} btn-primary`,
  "data-qa": dataQa = "UploadImagesButton",
  labelCTA = "Upload Image",
}: Props) => {
  const [uploadImages, { called }] = useUploadImage();
  const [totalImagesSetToUpload, setTotalImagesSetToUpload] =
    useState<number>(0);
  const [totalImagesUploaded, setTotalImagesUploaded] = useState<number>(0);
  const [uploading, setUploading] = useState<boolean>(false);
  const [pollImages, completedUploads] = usePollImages();

  useEffect(() => {
    // this listens to changes in completedUploads and updates the total images upload state.
    // this is used to get the 'x of y uploaded' copy.
    setTotalImagesUploaded(completedUploads.length);
  }, [completedUploads]);

  useEffect(() => {
    if (
      called &&
      totalImagesSetToUpload > 0 &&
      totalImagesSetToUpload === totalImagesUploaded
    ) {
      onComplete(completedUploads);
      setTotalImagesUploaded(0);
      setTotalImagesSetToUpload(0);
      setUploading(false);
    }
  }, [
    totalImagesSetToUpload,
    totalImagesUploaded,
    onComplete,
    completedUploads,
    called,
    setUploading,
  ]);

  const onChange = async (evt: ChangeEvent<HTMLInputElement>) => {
    const { files: imageFiles } = evt.currentTarget;
    if (imageFiles) {
      setUploading(true);
      const ids = await uploadImages({
        attachedToId,
        attachedToKind,
        sourcePlatform,
        imageFiles,
      });

      setTotalImagesSetToUpload(ids.length);
      pollImages(ids);
    }
  };

  return (
    <label
      htmlFor={`imageUploadFor${attachedToId}`}
      className={className}
      data-qa={dataQa}
    >
      <input
        type="file"
        name="imageUploadFor"
        id={`imageUploadFor${attachedToId}`}
        onChange={onChange}
        accept="image/*"
        multiple
        hidden
        disabled={totalImagesSetToUpload > 0 || uploading}
      />
      {totalImagesSetToUpload === 0 && !uploading && (
        <>
          {labelCTA} <i className="icon icon-upload" />
        </>
      )}
      {uploading &&
        totalImagesSetToUpload === 0 && ( // images being posted
          <LoadingDots>
            <>Uploading</>
          </LoadingDots>
        )}
      {totalImagesSetToUpload > 0 && ( // images posted and status polling
        <LoadingDots>
          <>
            {totalImagesUploaded} of {totalImagesSetToUpload} uploaded{" "}
          </>
        </LoadingDots>
      )}
    </label>
  );
};

export default UploadImagesButtons;
